import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Plus, Trash2, Tag, X } from "lucide-react";

interface Label {
  id: number;
  name: string;
  createdAt: string;
}

export function LabelsPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["labels"],
    queryFn: async () => {
      const res = await api.get("/v1/labels");
      if (!res.ok) throw new Error("Failed to fetch labels");
      return res.json() as Promise<{ data: Label[] }>;
    },
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => api.post("/v1/labels", { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labels"] });
      setNewName("");
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      api.patch(`/v1/labels/${id}`, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["labels"] });
      setEditingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/v1/labels/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["labels"] }),
  });

  const labels = data?.data || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <PageHeader
          title="Labels"
          description="Manage labels for offers, affiliates, and advertisers"
        />
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Label
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <Tag className="w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Label name..."
            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && newName.trim()) createMutation.mutate(newName.trim());
              if (e.key === "Escape") setShowForm(false);
            }}
          />
          <button
            onClick={() => newName.trim() && createMutation.mutate(newName.trim())}
            disabled={!newName.trim() || createMutation.isPending}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
          >
            {createMutation.isPending ? "Creating..." : "Create"}
          </button>
          <button
            onClick={() => setShowForm(false)}
            className="text-gray-400 hover:text-gray-600 p-2"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : labels.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <Tag className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No labels yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Name</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Created</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {labels.map((label: Label) => (
                <tr key={label.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    {editingId === label.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && editName.trim()) updateMutation.mutate({ id: label.id, name: editName.trim() });
                            if (e.key === "Escape") setEditingId(null);
                          }}
                        />
                        <button
                          onClick={() => editName.trim() && updateMutation.mutate({ id: label.id, name: editName.trim() })}
                          className="text-xs text-gray-900 font-medium hover:underline"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-xs text-gray-500 hover:underline"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setEditingId(label.id); setEditName(label.name); }}
                        className="inline-flex items-center gap-2 text-sm font-medium text-gray-900 hover:underline"
                      >
                        <Tag className="w-3.5 h-3.5 text-gray-400" />
                        {label.name}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(label.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete label "${label.name}"?`)) {
                          deleteMutation.mutate(label.id);
                        }
                      }}
                      className="text-gray-400 hover:text-red-600 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
